// ================================================================
//  InterceptorDownloadDialogController
//  "Download artifact" now opens this dialog instead of the old inline
//  per-environment dropdown menu — an environment picker, an editable
//  filename (pre-filled with the same systematic default the backend
//  itself computes), and a real "Download" step so the (multi-second —
//  Nexus fetch + repackage on the backend) wait has a visible spinner
//  instead of the browser just quietly saving a file some seconds after
//  the click.
//
//  Folder choice: only the File System Access API (window.showSaveFilePicker
//  — Chrome/Edge/Opera, not Firefox/Safari) can open a real native "Save As"
//  dialog from a web page; that's a browser platform limitation, not
//  something fixable here. Where it's available, Download opens that picker
//  (filename pre-filled/editable, any folder choosable) BEFORE starting the
//  network fetch, so cancelling it wastes nothing. Elsewhere, this falls
//  back to a plain <a download> — filename still editable, folder is
//  whatever the browser's own download settings dictate.
//
//  Locals injected:
//    - interceptor : one row of the biz-interceptors list (has
//                    .biz_interceptor_id, .biz_interceptor_name,
//                    .environments[]) — null in bundle mode
//    - bundle      : "Download All" mode, null otherwise —
//                    { ids, profileName, environments: [{ id, env_name }] }:
//                    one zip with the selected interceptors' artifacts for
//                    the chosen environment, a folder per interceptor
// ================================================================

angular.module('bizmetryApp').controller('InterceptorDownloadDialogController', function (
    $mdDialog, $mdToast, BizInterceptorService, interceptor, bundle
) {
    var vm = this;

    vm.bundle = bundle || null;
    vm.interceptor = interceptor || {};
    vm.environments = vm.bundle ? (vm.bundle.environments || []) : (vm.interceptor.environments || []);
    vm.selectedEnvironmentId = null;
    vm.filename = '';
    vm.downloading = false;
    vm.supportsSaveDialog = typeof window.showSaveFilePicker === 'function';

    var filenameManuallyEdited = false;

    vm.cancel = function () {
        if (vm.downloading) { return; }
        $mdDialog.cancel();
    };

    // Mirrors BizInterceptorServiceImpl#sanitizeForFileName — the backend's
    // own Content-Disposition filename isn't readable here (not in this
    // API's Access-Control-Expose-Headers), so the default matches the
    // same rule independently rather than falling back to an opaque id.
    function sanitizeForFileName(value) {
        if (!value) { return 'unknown'; }
        var sanitized = value.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-{2,}/g, '-');
        sanitized = sanitized.replace(/^-+|-+$/g, '');
        return sanitized || 'unknown';
    }

    function selectedEnvironment() {
        return vm.environments.filter(function (env) { return env.id === vm.selectedEnvironmentId; })[0] || null;
    }

    function defaultFilename(environment) {
        // Same names the server gives the downloads:
        //   bundle → interceptor-bundle-<profile name>-<environment>-<currentTimeMillis>
        //   single → <interceptor name>-<environment>-<currentTimeMillis>
        var environmentName = sanitizeForFileName(environment.env_name || environment.name || environment.id);
        if (vm.bundle) {
            return 'interceptor-bundle-' + sanitizeForFileName(vm.bundle.profileName) + '-' + environmentName + '-' + Date.now();
        }
        return sanitizeForFileName(vm.interceptor.biz_interceptor_name) + '-' + environmentName + '-' + Date.now();
    }

    // Only re-suggests the default when the user hasn't typed their own
    // name — picking a different environment shouldn't clobber an edit.
    vm.onEnvironmentChange = function () {
        var environment = selectedEnvironment();
        if (environment && !filenameManuallyEdited) {
            vm.filename = defaultFilename(environment);
        }
    };

    vm.onFilenameEdited = function () {
        filenameManuallyEdited = true;
    };

    vm.download = function () {
        var environment = selectedEnvironment();
        var name = (vm.filename || '').trim();
        if (!environment || !name || vm.downloading) { return; }

        var finalFileName = sanitizeForFileName(name) + '.zip';

        if (vm.supportsSaveDialog) {
            window.showSaveFilePicker({
                suggestedName: finalFileName,
                types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }]
            }).then(function (handle) {
                fetchAndDeliver(environment, function (arrayBuffer) {
                    return handle.createWritable().then(function (writable) {
                        return writable.write(arrayBuffer).then(function () { return writable.close(); });
                    });
                });
            }).catch(function (err) {
                // AbortError = the user closed/cancelled the native save
                // dialog — that's a normal outcome, not a failure to report.
                if (err && err.name !== 'AbortError') {
                    $mdToast.showSimple('Could not open the save dialog.');
                }
            });
            return;
        }

        fetchAndDeliver(environment, function (arrayBuffer) {
            var blob = new Blob([arrayBuffer], { type: 'application/zip' });
            var downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            downloadLink.download = finalFileName;
            downloadLink.click();
            URL.revokeObjectURL(downloadLink.href);
            return null;
        });

        function fetchAndDeliver(env, deliver) {
            vm.downloading = true;
            var request = vm.bundle
                ? BizInterceptorService.downloadBundle(vm.bundle.ids, env.id)
                : BizInterceptorService.downloadArtifact(vm.interceptor.biz_interceptor_id, env.id);
            request
                .then(function (arrayBuffer) {
                    return deliver(arrayBuffer);
                })
                .then(function () {
                    $mdDialog.hide();
                })
                .catch(function (message) {
                    $mdToast.showSimple(vm.bundle && typeof message === 'string'
                        ? message : 'Failed to download artifact.');
                })
                .finally(function () {
                    vm.downloading = false;
                });
        }
    };
});
